import { useAuth } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    ColorSchemeDefinition,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarSeparator,
    SidebarUserButton,
    THEMES,
    useSidebar,
    useTheme,
} from "@elit-hair/elit-ui";
import { Link } from "@tanstack/react-router";
import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbSettings, TbUser } from "react-icons/tb";

import { Greeting } from "@/components/greeting";
import { ClinicSelector } from "@/components/sidebar/clinic-selector";
import { NavigationItems } from "@/components/sidebar/navigation-items";
import { ENV } from "@/constants/env";
import { useRemoteConfigInput } from "@/pages/settings-page/utils/use-remote-config-input";
import { SETTINGS_KEYS, useSettings } from "@/providers/general/settings-provider";

type SidebarLinkTo =
    | string
    | {
          pathname?: string;
          search?: string;
          hash?: string;
      };

type SidebarLinkProps = {
    to: SidebarLinkTo;
    className?: string;
    children: ReactNode;
};

const SidebarLink = ({ to, className, children }: SidebarLinkProps) => {
    const href = typeof to === "string" ? to : `${to.pathname ?? "/"}${to.search ?? ""}${to.hash ?? ""}`;

    return (
        <Link to={href} className={className}>
            {children}
        </Link>
    );
};

export function SidebarLeft() {
    const { t } = useTranslation();
    const { userData, handleLogout } = useAuth();
    const { state } = useSidebar();
    const { theme, selectedColorScheme, toggleTheme, setSelectedColorScheme, colorSchemes } = useTheme();
    const { setValue } = useSettings();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    const { onChange: setTheme } = useRemoteConfigInput<THEMES>(SETTINGS_KEYS.THEME);

    const handleColorSchemeChange = useCallback(
        (scheme: ColorSchemeDefinition) => {
            setSelectedColorScheme(scheme);
            setValue(SETTINGS_KEYS.COLOR_SCHEME, scheme.value);
        },
        [setSelectedColorScheme, setValue]
    );

    // Handler to change elit-ui provider & remote config
    const handleThemeToggle = useCallback(() => {
        toggleTheme();
        setTheme(theme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
    }, [toggleTheme, setTheme, theme]);

    const navigationItems = [
        {
            path: "/profile",
            name: t("profile"),
            icon: TbUser,
        },
        {
            path: "/settings",
            name: t("settings"),
            icon: TbSettings,
        },
    ];

    return (
        <Sidebar side="left" variant="inset" collapsible="icon">
            <SidebarHeader>
                <ClinicSelector isCollapsed={state === "collapsed"} />
                <SidebarSeparator />
            </SidebarHeader>
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>{t("navigation")}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <NavigationItems />
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>

            <SidebarFooter>
                <SidebarMenu>
                    <SidebarSeparator />
                    <SidebarUserButton
                        userName={userData?.name || "User"}
                        greeting={<Greeting className="text-xs leading-none text-muted-foreground" />}
                        isLogoutDisabled={false}
                        navigationItems={navigationItems}
                        theme={theme}
                        onToggleTheme={handleThemeToggle}
                        colorScheme={selectedColorScheme}
                        colorSchemeOptions={colorSchemes}
                        onColorSchemeChange={handleColorSchemeChange}
                        onLogout={() => setShowLogoutDialog(true)}
                        linkComponent={SidebarLink}
                        logoutDialog={
                            <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>{t("logout_confirmation")}</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            {t("logout_confirmation_description")}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={() => {
                                                handleLogout();
                                                setShowLogoutDialog(false);
                                            }}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                            {t("logout")}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        }
                    />
                    <SidebarSeparator />
                    <p className="text-xs text-center pt-2 text-muted-foreground">
                        <span className="group-data-[collapsible=icon]:hidden">{t("app_version")}: </span>
                        <strong>{ENV.RELEASE_VERSION}</strong>
                    </p>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}
